import { RefObject, useEffect, useState } from 'react';

const SUMMARY_BILLING_ROOT_SELECTOR = '#gc-dg-summary #gc-dg-billing-address-root';
const ACTIVE_TAB_CONTENT_SELECTOR = '.gc-dg-tab-content.active';
const CARD_TAB_CONTENT_ID = 'gc-dg-card';

export type BillingPaymentMethod = 'ach' | 'card';

const findConsentBox = (tabContent: Element) =>
  Array.from(tabContent.children).find((child) => child.querySelector(':scope > h4'));

/**
 * @description Moves the SDK billing address from the order summary into the selected payment
 * method's form, above the consent box. The address is its own Preact app in
 * #gc-dg-billing-address-root, so moving that element keeps editing working.
 * @returns the payment method whose form holds the billing address, once it is placed
 */
export const useBillingAddressPlacement = (panelRef: RefObject<HTMLElement | null>) => {
  const [paymentMethod, setPaymentMethod] = useState<BillingPaymentMethod>();

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    let placedBillingRoot: Element | null = null;

    const placeBillingAddress = () => {
      // A new billing address from the SDK replaces the one moved before
      const summaryBillingRoot = panel.querySelector(SUMMARY_BILLING_ROOT_SELECTOR);
      const billingRoot = summaryBillingRoot ?? placedBillingRoot;
      const activeTabContent = panel.querySelector(ACTIVE_TAB_CONTENT_SELECTOR);
      const consentBox = activeTabContent && findConsentBox(activeTabContent);
      if (!billingRoot || !consentBox || consentBox.previousElementSibling === billingRoot) return;

      if (summaryBillingRoot) placedBillingRoot?.remove();
      consentBox.before(billingRoot);
      placedBillingRoot = billingRoot;
      setPaymentMethod(activeTabContent.id === CARD_TAB_CONTENT_ID ? 'card' : 'ach');
    };

    const observer = new MutationObserver(placeBillingAddress);
    observer.observe(panel, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    placeBillingAddress();

    return () => observer.disconnect();
  }, [panelRef]);

  return paymentMethod;
};
